import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { isEmptyArr } from '@utils/index';

import { generateRequestBodyFromNodes, getInvalidNodeIds } from '../helpers';
import { setDeletedEdges, setDeletedNodes, setIsSubmitted, setIsUpdate, setNodes, setOpenAIDrawer } from '../store';

import { useCreateChatFlow } from './use-create-chat-flow';
import { useCreateChatFlowBlock } from './use-create-chatflow-block';
import { useCreateChatFlowConnections } from './use-create-chatflow-connections';
import { useDeleteChatflowBlocks } from './use-delete-chatflow-blocks';
import { useDeleteChatflowConnections } from './use-delete-chatflow-connections';
import { useTrackFlowChanges } from './use-track-flow-changes';

export const onSave = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const segments = location.pathname.split('/');
  const agentId = segments[2];

  const deleteNodes = useDeleteChatflowBlocks();
  const deleteEdges = useDeleteChatflowConnections();
  const name = useAppSelector(state => state.aiAgentReducer.body?.name);
  const newNodes = generateRequestBodyFromNodes();
  const invalidNodeIds = getInvalidNodeIds(newNodes as any);
  const deletedNodes = useAppSelector(state => state.aiAgentReducer.deletedNodes);
  const deletedEdges = useAppSelector(state => state.aiAgentReducer.deletedEdges);
  const createChatFlow = useCreateChatFlow();
  const { resetChanges } = useTrackFlowChanges();
  const createChatFlowBlock = useCreateChatFlowBlock(resetChanges);
  const createChatFlowBlockConnection = useCreateChatFlowConnections(resetChanges);

  const onCloseDrawer = () => {
    dispatch(setOpenAIDrawer({ open: false, nodeType: undefined, nodeId: undefined }));
    dispatch(
      setNodes(nodes =>
        nodes.map(node => ({
          ...node,
          selected: false
        }))
      )
    );
  };

  const onCreate = useCallback(async () => {
    onCloseDrawer();
    dispatch(setIsSubmitted(true));

    const idsDeleteFromBack = deletedNodes?.filter(item => !item?.includes('new'));

    if (isEmptyArr(invalidNodeIds) && name?.length > 0) {
      try {
        if (idsDeleteFromBack && !isEmptyArr(idsDeleteFromBack)) {
          await deleteNodes.mutateAsync(idsDeleteFromBack.map(item => Number(item)));
        }
        if (deletedEdges && !isEmptyArr(deletedEdges)) {
          await deleteEdges.mutateAsync(deletedEdges.map(item => Number(item)));
        }

        const res = await createChatFlow.mutateAsync();
        const id = agentId !== 'new' ? agentId : res?.data?.id;

        await createChatFlowBlock.mutateAsync(id);
        await createChatFlowBlockConnection.mutateAsync(id);

        dispatch(setIsUpdate(false));
        dispatch(setIsSubmitted(false));
        dispatch(setDeletedNodes(undefined));
        dispatch(setDeletedEdges(undefined));
      } catch (error) {
        // optional: log or show error
      }
    }
  }, [
    deletedNodes,
    deletedEdges,
    invalidNodeIds,
    name,
    agentId,
    onCloseDrawer,
    dispatch,
    deleteNodes,
    deleteEdges,
    createChatFlow,
    createChatFlowBlock,
    createChatFlowBlockConnection
  ]);

  return {
    onCreate,
    isLoading: createChatFlow.isLoading || createChatFlowBlock.isLoading || createChatFlowBlockConnection.isLoading,
    isCreatingFlow: createChatFlow.isLoading,
    isCreatingBlocks: createChatFlowBlock.isLoading,
    isCreatingConnections: createChatFlowBlockConnection.isLoading
  };
};
